import { useCallback, useEffect, useRef, useState } from "react";
import { computeWidthToScroll } from "../utils/computeWidthToScroll";

const useRaceSelectorScroll = (raceId?: string, isMobile?: boolean) => {
  const listOfRacesRef = useRef<HTMLUListElement>(null);
  const [isOverFlowing, setIsOverFlowing] = useState(false);
  const [disableScrollRight, setDisableScrollRight] = useState(false);
  const [scrollAmount, setScrollAmount] = useState(0);

  const clientWidth = listOfRacesRef.current?.clientWidth;
  const scrollWidth = listOfRacesRef.current?.scrollWidth;

  const updateScrollPosition = useCallback(() => {
    if (listOfRacesRef.current) {
      setScrollAmount(listOfRacesRef.current.scrollLeft);
    }
  }, [raceId]);

  // Event listener on scroll helps keep track of scroll position,
  // for on click arrows and when scrollIntoView is executed
  useEffect(() => {
    if (listOfRacesRef.current) {
      listOfRacesRef.current.addEventListener("scroll", updateScrollPosition);
    }
    return () => {
      if (listOfRacesRef.current) {
        listOfRacesRef.current.removeEventListener(
          "scroll",
          updateScrollPosition
        );
      }
    };
  }, [raceId]);

  // Scroll right or left based on a value
  const handleScrollTo = useCallback(
    (value: number) => {
      if (listOfRacesRef.current) {
        listOfRacesRef.current.scrollLeft += value;
        updateScrollPosition();
      }
    },
    [raceId]
  );

  // Check if it is possible to scroll right
  useEffect(() => {
    if (listOfRacesRef.current) {
      const { offsetWidth } = listOfRacesRef.current;

      // TODO: it was added +1 because scrollLeft attribute gives a
      // value less than expected, to be solved in the future
      if (
        offsetWidth + scrollAmount + 1 >=
        listOfRacesRef.current.scrollWidth
      ) {
        setDisableScrollRight(true);
      } else {
        setDisableScrollRight(false);
      }
    }
  }, [scrollAmount]);

  // Check if list is overflowing
  useEffect(() => {
    if (listOfRacesRef.current) {
      const isOverflowing =
        !isMobile &&
        listOfRacesRef.current.scrollWidth > listOfRacesRef.current.clientWidth;
      setIsOverFlowing(isOverflowing);
    }
  }, [raceId]);

  const getWidthToScroll = (numberOfRaces: number) =>
    computeWidthToScroll(numberOfRaces, scrollWidth, clientWidth);

  return {
    listOfRacesRef,
    isOverFlowing,
    disableScrollRight,
    handleScrollTo,
    scrollAmount,
    getWidthToScroll
  } as const;
};

export default useRaceSelectorScroll;
